/**
 * Model classes whose fuel is a per-polygon code in ``model_subclass``.
 *
 * Two fuel-classification systems ride in that column: Scott & Burgan
 * (class 20, fuel-model numbers 101..204) and the Canadian FBP System
 * (class 30, fuel-type codes 1..24).  The vegetation editors show one
 * dropdown for whichever the chosen model class needs.
 */

import {
  SCOTT_BURGAN_FUEL_MODELS,
  formatScottBurganLabel,
  getScottBurganFuelModel,
  isScottBurganModelClass,
} from "./scottBurgan";
import { FBP_FUEL_TYPES, formatFbpLabel, getFbpFuelType, isFbpModelClass } from "./fbp";

export interface SubclassOption {
  value: number;
  label: string;
}

export interface SubclassSpec {
  /** Dropdown label, e.g. "Scott & Burgan Fuel Model". */
  label: string;
  options: SubclassOption[];
}

/** The subclass dropdown a model class needs, or null when it has none. */
export const subclassSpecFor = (modelClassName?: string | null): SubclassSpec | null => {
  if (isScottBurganModelClass(modelClassName)) {
    return {
      label: "Scott & Burgan Fuel Model",
      options: SCOTT_BURGAN_FUEL_MODELS.map((fm) => ({
        value: fm.number,
        label: formatScottBurganLabel(fm),
      })),
    };
  }
  if (isFbpModelClass(modelClassName)) {
    return {
      label: "FBP Fuel Type",
      options: FBP_FUEL_TYPES.map((ft) => ({ value: ft.code, label: formatFbpLabel(ft) })),
    };
  }
  return null;
};

/** True when polygons of this model class must carry a ``model_subclass``. */
export const hasSubclass = (modelClassName?: string | null): boolean =>
  subclassSpecFor(modelClassName) !== null;

/** Short code for a subclass value under a model class (``GR2``, ``C-2``), or null. */
export const subclassCode = (
  modelClassName: string | null | undefined,
  subclass: number | null | undefined,
): string | null => {
  if (isScottBurganModelClass(modelClassName)) return getScottBurganFuelModel(subclass)?.code ?? null;
  if (isFbpModelClass(modelClassName)) return getFbpFuelType(subclass)?.name ?? null;
  return null;
};
